import { splitFences } from './passes/fenceSplit';
import { htmlInlineToMarkdown } from './passes/htmlInlineToMarkdown';
import { typoTolerance } from './passes/typoTolerance';
import { whitespaceAttrs } from './passes/whitespaceAttrs';
import { hallucinatedAlias } from './passes/hallucinatedAlias';
import { partialStreamCut } from './passes/partialStreamCut';
import { toolCallLeak } from './passes/toolCallLeak';
import type { ToolCallLeak, ToolCallLeakResult } from './passes/toolCallLeak';
import { thinkTagLeak } from './passes/thinkTagLeak';
import { repetitionCollapse } from './passes/repetitionCollapse';

export { normalizeSearchText } from './passes/searchText';
export type { ToolCallLeak, ToolCallLeakResult } from './passes/toolCallLeak';
export type { ThinkTagLeak, ThinkTagLeakResult } from './passes/thinkTagLeak';
export { repetitionCollapse } from './passes/repetitionCollapse';

export interface NormalizeOptions {
  /**
   * Si true, tronque un éventuel tag partiel en fin de buffer
   * (typewriter en cours). Default: false.
   */
  streaming?: boolean;
}

/**
 * Normalise le texte émis par DeepSeek V4 Flash avant qu'il sorte du
 * buffer WebSocket. Appelé à CHAQUE frontière front (renderer, DOCX,
 * clipboard, sidebar).
 *
 * Pipeline en 9 passes ordonnées :
 *  0. fenceSplit          — segmente prose / code (fences + inline code)
 *  0b. toolCallLeak        — retire les appels d'outils écrits en prose (prose only)
 *  0c. thinkTagLeak        — retire les fuites de raisonnement <think>/<thinking> (prose only)
 *  0d. repetitionCollapse  — réduit une répétition résiduelle figée à une occurrence (prose only)
 *  1. htmlInlineToMarkdown — <strong>→**, <em>→*, <br>→\n   (prose only)
 *  1b. typoTolerance       — <\hector-callot> → </hector-callout> (prose only, distance ≤ 1)
 *  3. hallucinatedAlias    — <hector-info> → <hector-callout type="info"> (prose only, strip attrs)
 *  2. whitespaceAttrs      — tolérance attrs Hector
 *  4. partialStreamCut     — tronque tag partiel en fin (si streaming)
 *
 * Garanties :
 *  - Pure : normalize(normalize(x)) === normalize(x)
 *  - Conservative : tag inconnu laissé intact
 *  - Fence-safe : code blocks et inline code jamais réécrits
 */
export function normalizeAssistantText(
  raw: string,
  opts?: NormalizeOptions
): string {
  if (!raw) return raw;

  // Passe 0 : segmenter
  const segments = splitFences(raw);

  // Toutes les passes de réécriture sur prose uniquement (fence-safe)
  let recomposed = segments
    .map((seg) => {
      if (seg.kind === 'code') return seg.content;
      let prose = seg.content;
      // Passes 0b/0c AVANT toute réécriture : le corps d'un <parameter> ou d'un
      // <think> peut contenir du HTML ou des coquilles que les passes suivantes
      // tenteraient de « corriger » alors que le bloc entier part à la poubelle.
      prose = toolCallLeak(prose).text;   // pass 0b — appels d'outils écrits en prose
      prose = thinkTagLeak(prose).text;   // pass 0c — raisonnement <think>/<thinking> fuité
      prose = repetitionCollapse(prose);  // pass 0d — répétition résiduelle figée
      prose = htmlInlineToMarkdown(prose); // pass 1
      prose = typoTolerance(prose);        // pass 1b — coquilles légères avant alias
      prose = hallucinatedAlias(prose);    // pass 3
      prose = whitespaceAttrs(prose);      // pass 2 — attrs Hector (espaces, apostrophes, guillemets typographiques)
      return prose;
    })
    .join('');

  // Passe 4 : partial cut (streaming)
  if (opts?.streaming) {
    recomposed = partialStreamCut(recomposed);
  }

  return recomposed;
}

/**
 * Variante fence-safe de la passe `toolCallLeak`, exposée pour le BACKEND.
 *
 * Le front passe par `normalizeAssistantText` (la passe y est déjà câblée). Le
 * backend, lui, a besoin du verdict `leaks` en plus du texte : la MasterLoop
 * nettoie le tour AVANT persistance et, si le modèle n'a produit AUCUN appel
 * natif, relance le tour au lieu de le rendre mort. Voir
 * `passes/toolCallLeak.ts` pour le pourquoi de l'incident.
 *
 * Fence-safe : un exemple d'appel d'outil montré dans un bloc ``` ou en code
 * inline est légitime (documentation, skill) — il traverse intact et ne compte
 * pas comme fuite.
 */
export function stripTextEmittedToolCalls(raw: string): ToolCallLeakResult {
  if (!raw) return { text: raw, leaks: [] };

  const leaks: ToolCallLeak[] = [];
  const text = splitFences(raw)
    .map((seg) => {
      if (seg.kind === 'code') return seg.content;
      const result = toolCallLeak(seg.content);
      leaks.push(...result.leaks);
      return result.text;
    })
    .join('');

  return { text: leaks.length > 0 ? text.trimEnd() : raw, leaks };
}
